/**
 * Everything that happened to a person, newest first: the ledger, dues payments (and the ones that failed or
 * went back), sign-ups and orders, calls, notes and reviews, each with its own icon (the ones Today's feed
 * uses). Emails sent are not here: Keystone mirrors no ActiveCampaign campaign activity yet.
 */
import { useState } from "react";
import type { OutreachChannel } from "../../api/outreach.types";
import type { DuesOrderRow, EngagementItem, PersonFull } from "../../api/person360.types";
import { fmtWhen } from "../data/format";
import { Button, Icon, SheetSection, STANDING_LABEL, Timeline, TimelineItem, type IconName, type StandingValue } from "../kit";
import { usd } from "../screens/settings/format";
import { EVENT_LABEL, fmtDate, type LedgerEvent } from "./format";
import { usePending, type Pending } from "./pending";

interface Entry {
  key: string;
  icon: IconName;
  title: string;
  /** ISO, for the order. */
  at: string;
  time: string;
  body?: string;
}

const SHOWN = 5;
const EVENT_ICON: Record<LedgerEvent, IconName> = {
  joined: "feed-user",
  reinstated: "feed-user",
  // A lapse is a change of standing, not a failure: red (feed-alert) stays for what failed.
  lapsed: "feed-repeat",
  dropped: "feed-repeat",
  bucket_changed: "feed-repeat",
  baseline: "feed-book",
  payment_issue: "feed-alert",
  payment_recovered: "status-delivered",
};

/** A ledger event's words and icon. A type the ledger gains later reads as its own words, never as a crash. */
function ledgerEvent(type: string): { title: string; icon: IconName } {
  const known = type in EVENT_LABEL ? (type as LedgerEvent) : null;
  return known ? { title: EVENT_LABEL[known], icon: EVENT_ICON[known] } : { title: type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, " "), icon: "feed-repeat" };
}
const CHANNEL: Record<OutreachChannel, string> = { call: "Call", email: "Email", text: "Text", in_person: "Visit", other: "Contact" };

/** "Lapsed → Active"; nothing when the ledger does not name both ends, or when the title already says where they went (a join, a lapse). */
function movedText(from: string | null, to: string | null, title: string): string | undefined {
  const word = (key: string | null) => (key && key in STANDING_LABEL ? STANDING_LABEL[key as StandingValue] : null);
  return word(from) && word(to) && word(to) !== title ? `${word(from)} → ${word(to)}` : undefined;
}

/** What a dues order's payment status says happened, in the money words of `backend/apps/commerce/payment_status.py`. */
function duesTitle(o: DuesOrderRow): { title: string; icon: IconName } {
  const s = (o.payment_status ?? "").toLowerCase();
  if (o.is_pending_check) return { title: "Cheque pending", icon: "feed-alert" };
  if (s.startsWith("failed") || s === "chargeback" || s.startsWith("dead") || s.startsWith("deleted")) return { title: "Payment failed", icon: "feed-alert" };
  if (s.includes("refunded")) return { title: "Refunded", icon: "feed-repeat" };
  if (s.startsWith("pending") || s === "addpaymentneeded" || s === "review") return { title: "Dues owed", icon: "status-progress" };
  return { title: "Dues paid", icon: "status-delivered" };
}

/** An order whose CE21 account is gone, linked to this person by its email or by a name staff confirmed (#715). */
const MATCHED = { email: "Matched by email: CE21 account deleted", name: "Matched by name: CE21 account deleted", merge: "Merged in CE21: CE21 account deleted" } as const;
const matched = (by: keyof typeof MATCHED | null | undefined): string | false => !!by && MATCHED[by];

/** "Doctor membership · $395.00". */
const duesBody = (o: DuesOrderRow): string => [o.product, usd(o.amount_cents / 100), matched(o.matched_by)].filter(Boolean).join(" · ");

/** A CE21 registration (a course, a live event) or a non-dues order line (an event, the store). */
const ENGAGEMENT_TITLE: Record<EngagementItem["kind"], string> = { registration: "Registered", order_item: "Ordered" };

function entriesOf({ lifecycle, notes, acknowledgements, payments, engagement }: PersonFull): Entry[] {
  const out: Entry[] = [
    ...payments.last_12_dues_orders.flatMap((o, i) => (o.date ? [{ key: `dues:${i}`, ...duesTitle(o), at: o.date, time: fmtDate(o.date), body: duesBody(o) }] : [])),
    ...engagement.items.flatMap((e, i) => (e.date ? [{ key: `engagement:${i}`, icon: "calendar" as const, title: ENGAGEMENT_TITLE[e.kind], at: e.date, time: fmtDate(e.date), body: [e.product_name ?? e.product_type, matched(e.matched_by)].filter(Boolean).join(" · ") }] : [])),
    ...lifecycle.events.map((e) => {
      const { title, icon } = ledgerEvent(e.eventType);
      return { key: `ledger:${e.id}`, icon, title, at: e.effectiveAt, time: fmtDate(e.effectiveAt), body: movedText(e.fromStanding, e.toStanding, title) };
    }),
    ...notes.outreach_entries.map((e) => ({ key: `entry:${e.id}`, icon: "headset" as const, title: CHANNEL[e.channel as OutreachChannel] ?? "Contact", at: e.occurred_at, time: fmtWhen(e.occurred_at) ?? "", body: e.summary })),
    ...notes.staff_notes.map((n) => ({ key: `note:${n.id}`, icon: "comment" as const, title: "Note", at: n.created_at, time: fmtWhen(n.created_at) ?? "", body: n.body })),
    ...acknowledgements.items.filter((a) => !a.is_deleted).map((a) => ({ key: `ack:${a.id}`, icon: "status-delivered" as const, title: "Reviewed", at: a.acknowledged_at, time: fmtWhen(a.acknowledged_at) ?? "", body: a.acknowledged_by_name })),
  ];
  return out.sort((a, b) => b.at.localeCompare(a.at));
}

/** A call or a note saved a moment ago, in the words its record will have. */
const pendingEntry = (p: Pending): Entry => ({ key: p.key, icon: p.kind === "call" ? "headset" : "comment", title: p.kind === "call" ? "Call" : "Note", at: p.at, time: fmtWhen(p.at) ?? "", body: p.body });

export function History({ person }: { person: PersonFull }) {
  const [all, setAll] = useState(false);
  const pending = usePending(person.identity.id);
  const entries = [...pending.map(pendingEntry), ...entriesOf(person)].sort((a, b) => b.at.localeCompare(a.at));
  if (entries.length === 0) return null;
  const shown = all ? entries : entries.slice(0, SHOWN);
  return (
    <SheetSection title="Timeline">
      <Timeline className="flex-none overflow-visible">
        {shown.map((e, i) => (
          // No entrance of its own: the sheet carries the content in, rows that rise after it read as a second, ghosted copy.
          <TimelineItem key={e.key} icon={<Icon name={e.icon} />} title={e.title} time={e.time} last={i === shown.length - 1} className="animate-none!">
            {e.body}
          </TimelineItem>
        ))}
      </Timeline>
      {entries.length > SHOWN && (
        <Button variant="ghost" size="sm" className="-ml-2 self-start" aria-expanded={all} onClick={() => setAll(!all)}>
          {all ? "Show fewer" : "Show all"}
        </Button>
      )}
    </SheetSection>
  );
}
